import { format, parseISO } from 'date-fns';
import { enUS, es, fr, it, pl } from 'date-fns/locale';
import { CalendarIcon } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { resolveLocale } from '@/i18n';
import { cn } from '@/lib/utils';

const localeMap = { en: enUS, pl, fr, es, it } as const;

type DatePickerProps = {
  /** ISO date (yyyy-MM-dd) or undefined when empty. */
  value: string | undefined;
  onChange: (value: string | undefined) => void;
  placeholder?: string;
  /** Disables the whole control. */
  disabled?: boolean;
  /** react-day-picker matcher for non-selectable days. */
  disabledDates?: React.ComponentProps<typeof Calendar>['disabled'];
  className?: string;
  id?: string;
  'aria-label'?: string;
  /** Use a modal popover (required when rendered inside a Radix Dialog, else the
   * portaled calendar click is treated as "outside" and the picker closes without
   * selecting). Leave false on pages so it doesn't lock scroll. */
  modal?: boolean;
};

/**
 * App-wide date picker (Popover + Calendar). The single source of truth for date
 * inputs - used directly for filters and wrapped by FormDateField for forms. The
 * value is the calendar day in LOCAL time (date-fns `format`), avoiding the
 * UTC off-by-one that `toISOString()` causes in negative/positive-offset zones.
 */
export function DatePicker({
  value,
  onChange,
  placeholder,
  disabled,
  disabledDates,
  className,
  id,
  'aria-label': ariaLabel,
  modal = false,
}: DatePickerProps) {
  const { i18n, t } = useTranslation();
  const locale = localeMap[resolveLocale(i18n.language)];
  const [open, setOpen] = useState(false);

  const date = value ? parseISO(value) : undefined;

  return (
    <Popover open={open} onOpenChange={setOpen} modal={modal}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          id={id}
          aria-label={ariaLabel}
          disabled={disabled}
          className={cn(
            'h-9 w-full justify-start rounded-[10px] border-[#e5e5e5] bg-white px-3 text-left text-sm font-normal shadow-none hover:bg-white',
            !value && 'text-[#737373]',
            className,
          )}
        >
          <CalendarIcon className="mr-2 size-4 opacity-60" />
          {date ? format(date, 'PP', { locale }) : (placeholder ?? t('actions.select'))}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={date}
          onSelect={(selected) => {
            onChange(selected ? format(selected, 'yyyy-MM-dd') : undefined);
            setOpen(false);
          }}
          disabled={disabledDates}
          locale={locale}
        />
      </PopoverContent>
    </Popover>
  );
}
